import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { businessReportsApi, type HospitalCashFlowLine } from '@/api/businessReports';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

function formatDisplay(cellFormat: HospitalCashFlowLine['format'], value: number): string {
  if (cellFormat === 'currency') {
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  return value.toLocaleString('en-IN');
}

function formatEditValue(cellFormat: HospitalCashFlowLine['format'], value: number): string {
  if (cellFormat === 'currency') return String(Math.round(value));
  return String(value);
}

function parseEditInput(cellFormat: HospitalCashFlowLine['format'], raw: string): number | null {
  const cleaned = raw.replace(/[₹,\s]/g, '').trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return cellFormat === 'currency' ? Math.round(n) : n;
}

type CashFlowEditableCellProps = {
  cell?: HospitalCashFlowLine;
  rowFormat: HospitalCashFlowLine['format'];
  periodKey: string;
  periodMode: 'MTD' | 'YTD';
  hospitalId?: string;
  queryKey: unknown[];
  editable: boolean;
};

export function CashFlowEditableCell({
  cell,
  rowFormat,
  periodKey,
  periodMode,
  hospitalId,
  queryKey,
  editable,
}: CashFlowEditableCellProps) {
  const queryClient = useQueryClient();
  const fmt = cell?.format ?? rowFormat;
  const value = cell?.value ?? 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const saveMutation = useMutation({
    mutationFn: (nextValue: number) =>
      businessReportsApi.hospitalCashFlowOverrideUpsert({
        lineId: cell?.id ?? '',
        value: nextValue,
        periodKey,
        periodMode,
        hospitalId,
      }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey });
      setEditing(false);
      toast.success('Cash Flow value updated');
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save';
      toast.error(msg);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      businessReportsApi.hospitalCashFlowOverrideDelete({
        lineId: cell?.id ?? '',
        periodKey,
        periodMode,
        hospitalId,
      }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey });
      toast.success('Reverted to system value');
    },
    onError: () => toast.error('Failed to reset'),
  });

  const startEdit = () => {
    if (!editable || !cell) return;
    setDraft(formatEditValue(fmt, value));
    setEditing(true);
  };

  const commitEdit = () => {
    if (!cell) return;
    const parsed = parseEditInput(fmt, draft);
    if (parsed == null) {
      toast.error('Enter a valid number');
      setEditing(false);
      return;
    }
    if (parsed === value) {
      setEditing(false);
      return;
    }
    saveMutation.mutate(parsed);
  };

  const tooltip =
    cell?.isOverridden && cell.computedValue != null
      ? `System: ${formatDisplay(fmt, cell.computedValue)} (double-click to edit, right-click to reset)`
      : editable
        ? 'Double-click to edit'
        : undefined;

  if (!editable || !cell) {
    return <span>{formatDisplay(fmt, value)}</span>;
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        className="h-8 w-28 ml-auto text-right text-sm"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitEdit();
          if (e.key === 'Escape') setEditing(false);
        }}
        disabled={saveMutation.isPending}
      />
    );
  }

  return (
    <button
      type="button"
      title={tooltip}
      className={cn(
        'w-full text-right whitespace-nowrap rounded px-1 py-0.5',
        'cursor-pointer hover:bg-amber-100/80',
        cell.isOverridden && 'bg-amber-50 font-medium text-amber-950 ring-1 ring-amber-200/80'
      )}
      onDoubleClick={startEdit}
      onContextMenu={(e) => {
        if (!cell.isOverridden) return;
        e.preventDefault();
        resetMutation.mutate();
      }}
    >
      {formatDisplay(fmt, value)}
    </button>
  );
}
